/**
 * Translation provider module with fallback chain.
 *
 * Features:
 *  1. Provider chain    — tries each provider in order; untranslated texts
 *                         fall through to the next provider automatically.
 *  2. localStorage      — translations survive page reloads.
 *  3. Batching          — texts queued within BATCH_DELAY ms are sent together.
 *  4. No infinite retry — on total failure, original text is cached as fallback.
 *
 * ── Configure the chain ──────────────────────────────────────────────────────
 * Edit PROVIDER_CHAIN to set order and which providers are active.
 * Available providers (no key required unless noted):
 *
 *   "google-free"  – Unofficial Google endpoint. No key. Fast, unreliable TOS.
 *   "lingva"       – Lingva public instance. No key. Open-source Google proxy.
 *   "mymemory"     – MyMemory free API. No key (1k words/day).
 *                    Set VITE_MYMEMORY_EMAIL in .env for 10k words/day.
 *   "libretranslate" – LibreTranslate public instance. No key (rate-limited).
 *                    Set VITE_LIBRETRANSLATE_URL + VITE_LIBRETRANSLATE_KEY to
 *                    use a self-hosted or paid instance instead.
 *   "azure"        – Microsoft Azure Translator. Free: 2M chars/month.
 *                    Requires VITE_AZURE_TRANSLATOR_KEY + VITE_AZURE_TRANSLATOR_REGION.
 *   "yandex"       – Yandex Translate. Free: 1M chars/month.
 *                    Requires VITE_YANDEX_TRANSLATE_KEY.
 *   "ibm"          – IBM Watson Language Translator. Free Lite: 1M chars/month.
 *                    Requires VITE_IBM_WATSON_KEY + VITE_IBM_WATSON_URL.
 *   "deepl"        – DeepL API free tier. 500k chars/month.
 *                    Requires VITE_DEEPL_API_KEY (key ends with ":fx" for free tier).
 *   "google"       – Official Google Cloud Translation API v2.
 *                    Requires VITE_GOOGLE_TRANSLATE_API_KEY.
 *
 * Recommended production chain (most free quota, best coverage):
 *   ["google-free", "lingva", "mymemory", "libretranslate", "yandex", "ibm", "azure", "deepl", "google"]
 */
const PROVIDER_CHAIN = [
    { name: "google-free",    enabled: true  },
    { name: "lingva",         enabled: true  },
    { name: "mymemory",       enabled: true  },
    { name: "libretranslate", enabled: true  },
    { name: "yandex",         enabled: false },
    { name: "ibm",            enabled: false },
    { name: "azure",        enabled: false },
    { name: "deepl",          enabled: false },
    { name: "google",         enabled: false  },
];

const STORAGE_KEY = "gt_cache";
const BATCH_DELAY = 50; // ms to collect texts before flushing

// ── Cache ─────────────────────────────────────────────────────────────────────
// Structure: { [lang]: { [text]: translation } }
let cache = {};
try {
    cache = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
} catch {
    cache = {};
}

const persistCache = () => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    } catch { /* quota exceeded */ }
};

// ── Subscribers ───────────────────────────────────────────────────────────────
const subscribers = new Set();

export const subscribeToTranslations = (cb) => {
    subscribers.add(cb);
    return () => subscribers.delete(cb);
};

const notify = () => subscribers.forEach((cb) => cb());

// ── Public read helpers ───────────────────────────────────────────────────────
const pendingKey = (text, lang) => `${text}|${lang}`;

export const getCached   = (text, lang) => cache[lang]?.[text] ?? null;
export const isLoading   = (text, lang) => pendingKeys.has(pendingKey(text, lang));

// ── Batch queue ───────────────────────────────────────────────────────────────
const pendingKeys = new Set();
let batchQueue    = new Map(); // lang → Set<text>
let batchTimer    = null;

const flushBatch = () => {
    batchTimer = null;
    const snapshot = batchQueue;
    batchQueue = new Map();
    snapshot.forEach((texts, lang) => {
        if (texts.size) sendBatch([...texts], lang);
    });
};

const queueText = (text, lang) => {
    if (!batchQueue.has(lang)) batchQueue.set(lang, new Set());
    batchQueue.get(lang).add(text);
    if (!batchTimer) batchTimer = setTimeout(flushBatch, BATCH_DELAY);
};

// ── Provider implementations ──────────────────────────────────────────────────
// Each provider is an async function:
//   (texts: string[], lang: string) => Promise<{ [original]: translated }>
// Returning a partial object is fine — missing keys fall through to next provider.
// Throwing causes the orchestrator to skip to the next provider.

const SENTINEL = "\n~~~\n";

const providers = {
    "google-free": async (texts, lang) => {
        const url = `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${lang}&dt=t&q=${encodeURIComponent(texts.join(SENTINEL))}`;
        const res  = await fetch(url);
        const data = await res.json();

        const full  = (data?.[0] ?? []).map((item) => item?.[0] ?? "").join("");
        const parts = full.split(/\n\s*~~~\s*\n/).map((s) => s.trim());

        const result = {};
        texts.forEach((original, i) => {
            if (parts[i]) result[original] = parts[i];
        });
        return result;
    },

    "mymemory": async (texts, lang) => {
        const email      = import.meta.env.VITE_MYMEMORY_EMAIL ?? "";
        const emailParam = email ? `&de=${encodeURIComponent(email)}` : "";

        const settled = await Promise.allSettled(
            texts.map((text) =>
                fetch(
                    `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${lang}${emailParam}`
                )
                    .then((res) => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.json();
                    })
                    .then((data) => {
                        const t = data?.responseData?.translatedText;
                        if (!t) throw new Error("empty");
                        return t;
                    })
            )
        );

        const result = {};
        texts.forEach((original, i) => {
            if (settled[i].status === "fulfilled") result[original] = settled[i].value;
        });
        return result;
    },

    "deepl": async (texts, lang) => {
        const API_KEY = import.meta.env.VITE_DEEPL_API_KEY ?? "";
        if (!API_KEY) throw new Error("VITE_DEEPL_API_KEY not set");

        const base = API_KEY.endsWith(":fx")
            ? "https://api-free.deepl.com/v2/translate"
            : "https://api.deepl.com/v2/translate";

        const res  = await fetch(base, {
            method:  "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization:  `DeepL-Auth-Key ${API_KEY}`,
            },
            body: JSON.stringify({ text: texts, source_lang: "EN", target_lang: lang.toUpperCase() }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const translated = data?.translations ?? [];
        const result = {};
        texts.forEach((original, i) => {
            if (translated[i]?.text) result[original] = translated[i].text;
        });
        return result;
    },

    // Lingva — open-source Google Translate proxy. No key needed.
    // Uses a public instance; self-hostable for higher limits.
    "lingva": async (texts, lang) => {
        const BASE = import.meta.env.VITE_LINGVA_URL ?? "https://lingva.ml";

        const settled = await Promise.allSettled(
            texts.map((text) =>
                fetch(`${BASE}/api/v1/en/${lang}/${encodeURIComponent(text)}`)
                    .then((res) => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.json();
                    })
                    .then((data) => {
                        const t = data?.translation;
                        if (!t) throw new Error("empty");
                        return t;
                    })
            )
        );

        const result = {};
        texts.forEach((original, i) => {
            if (settled[i].status === "fulfilled") result[original] = settled[i].value;
        });
        return result;
    },

    // LibreTranslate — open-source, self-hostable. Free public instance is rate-limited.
    // Set VITE_LIBRETRANSLATE_URL to use your own instance.
    // Set VITE_LIBRETRANSLATE_KEY if your instance requires an API key.
    "libretranslate": async (texts, lang) => {
        const BASE    = import.meta.env.VITE_LIBRETRANSLATE_URL ?? "https://libretranslate.com";
        const API_KEY = import.meta.env.VITE_LIBRETRANSLATE_KEY ?? "";

        const settled = await Promise.allSettled(
            texts.map((text) =>
                fetch(`${BASE}/translate`, {
                    method:  "POST",
                    headers: { "Content-Type": "application/json" },
                    body:    JSON.stringify({
                        q:      text,
                        source: "en",
                        target: lang,
                        format: "text",
                        ...(API_KEY ? { api_key: API_KEY } : {}),
                    }),
                })
                    .then((res) => {
                        if (!res.ok) throw new Error(`HTTP ${res.status}`);
                        return res.json();
                    })
                    .then((data) => {
                        const t = data?.translatedText;
                        if (!t) throw new Error("empty");
                        return t;
                    })
            )
        );

        const result = {};
        texts.forEach((original, i) => {
            if (settled[i].status === "fulfilled") result[original] = settled[i].value;
        });
        return result;
    },

    // Microsoft Azure Translator — 2M chars/month free tier.
    // Requires VITE_AZURE_TRANSLATOR_KEY + VITE_AZURE_TRANSLATOR_REGION in .env.
    "azure": async (texts, lang) => {
        const API_KEY = import.meta.env.VITE_AZURE_TRANSLATOR_KEY ?? "";
        const REGION  = import.meta.env.VITE_AZURE_TRANSLATOR_REGION ?? "";
        if (!API_KEY) throw new Error("VITE_AZURE_TRANSLATOR_KEY not set");

        const res  = await fetch(
            `https://api.cognitive.microsofttranslator.com/translate?api-version=3.0&from=en&to=${lang}`,
            {
                method:  "POST",
                headers: {
                    "Content-Type":                    "application/json",
                    "Ocp-Apim-Subscription-Key":       API_KEY,
                    "Ocp-Apim-Subscription-Region":    REGION,
                },
                body: JSON.stringify(texts.map((text) => ({ Text: text }))),
            }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const result = {};
        texts.forEach((original, i) => {
            const t = data?.[i]?.translations?.[0]?.text;
            if (t) result[original] = t;
        });
        return result;
    },

    // Yandex Translate — free tier: 1M chars/month.
    // Create key at translate.yandex.com/developers.
    // Requires VITE_YANDEX_TRANSLATE_KEY in .env.
    "yandex": async (texts, lang) => {
        const API_KEY = import.meta.env.VITE_YANDEX_TRANSLATE_KEY ?? "";
        if (!API_KEY) throw new Error("VITE_YANDEX_TRANSLATE_KEY not set");

        const res  = await fetch("https://translate.api.cloud.yandex.net/translate/v2/translate", {
            method:  "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization:  `Api-Key ${API_KEY}`,
            },
            body: JSON.stringify({
                texts,
                sourceLanguageCode: "en",
                targetLanguageCode: lang,
            }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const translated = data?.translations ?? [];
        const result = {};
        texts.forEach((original, i) => {
            if (translated[i]?.text) result[original] = translated[i].text;
        });
        return result;
    },

    // IBM Watson Language Translator — Lite plan: 1M chars/month free.
    // Create instance at cloud.ibm.com → Watson → Language Translator.
    // Requires VITE_IBM_WATSON_KEY + VITE_IBM_WATSON_URL in .env.
    "ibm": async (texts, lang) => {
        const API_KEY      = import.meta.env.VITE_IBM_WATSON_KEY ?? "";
        const INSTANCE_URL = import.meta.env.VITE_IBM_WATSON_URL ?? "";
        if (!API_KEY || !INSTANCE_URL) throw new Error("VITE_IBM_WATSON_KEY / VITE_IBM_WATSON_URL not set");

        const res  = await fetch(`${INSTANCE_URL}/v3/translate?version=2018-05-01`, {
            method:  "POST",
            headers: {
                "Content-Type": "application/json",
                Authorization:  `Basic ${btoa(`apikey:${API_KEY}`)}`,
            },
            body: JSON.stringify({ text: texts, source: "en", target: lang }),
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const translated = data?.translations ?? [];
        const result = {};
        texts.forEach((original, i) => {
            if (translated[i]?.translation) result[original] = translated[i].translation;
        });
        return result;
    },

    "google": async (texts, lang) => {
        const API_KEY = import.meta.env.VITE_GOOGLE_TRANSLATE_API_KEY ?? "";
        if (!API_KEY) throw new Error("VITE_GOOGLE_TRANSLATE_API_KEY not set");

        const res  = await fetch(
            `https://translation.googleapis.com/language/translate/v2?key=${API_KEY}`,
            {
                method:  "POST",
                headers: { "Content-Type": "application/json" },
                body:    JSON.stringify({ q: texts, source: "en", target: lang }),
            }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();

        const translated = data?.data?.translations ?? [];
        const result = {};
        texts.forEach((original, i) => {
            if (translated[i]?.translatedText) result[original] = translated[i].translatedText;
        });
        return result;
    },
};

// ── Orchestrator ──────────────────────────────────────────────────────────────
// Walks through PROVIDER_CHAIN. After each provider, texts that got a
// translation are cached; the rest are passed to the next provider.
// Anything still untranslated after all providers falls back to the original.

const sendBatch = async (texts, lang) => {
    texts.forEach((t) => pendingKeys.add(pendingKey(t, lang)));
    notify(); // signal loading started

    let remaining = [...texts];

    for (const { name, enabled } of PROVIDER_CHAIN) {
        if (!remaining.length) break;
        if (!enabled) continue;

        const fn = providers[name];
        if (!fn) continue;

        try {
            const results = await fn(remaining, lang);

            if (!cache[lang]) cache[lang] = {};
            const stillMissing = [];

            remaining.forEach((original) => {
                const translated = results[original];
                if (translated) {
                    cache[lang][original] = translated;
                } else {
                    stillMissing.push(original);
                }
            });

            remaining = stillMissing;
            persistCache();
            notify(); // show partial results as they arrive
        } catch {
            // provider failed entirely — leave remaining unchanged, try next
        }
    }

    // Anything still untranslated — cache original as fallback
    if (remaining.length) {
        if (!cache[lang]) cache[lang] = {};
        remaining.forEach((t) => { cache[lang][t] = t; });
        persistCache();
    }

    texts.forEach((t) => pendingKeys.delete(pendingKey(t, lang)));
    notify(); // final update
};

// ── Public entry point ────────────────────────────────────────────────────────
/**
 * Request a translation. Results land asynchronously in the cache;
 * all subscribers are notified so they can re-render.
 *
 * @param {string} text       - English source text
 * @param {string} targetLang - BCP-47 language code, e.g. "fr"
 */
export const fetchTranslation = (text, targetLang) => {
    if (!text) return;

    // English — no API call needed
    if (targetLang === "en") {
        if (!cache.en) cache.en = {};
        if (!cache.en[text]) { cache.en[text] = text; persistCache(); }
        return;
    }

    if (cache[targetLang]?.[text]) return;
    if (pendingKeys.has(pendingKey(text, targetLang))) return;
    if (batchQueue.get(targetLang)?.has(text)) return;
    queueText(text, targetLang);
};
